import { Send } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { InfiniteData } from "@tanstack/react-query";
import { authenticatedFetch } from "../../../../utils/authenticatedFetch";
import { Post, CurrentUser } from "../../../../types/types";

interface CommentInputProps {
  post: Post;
  currentUser: CurrentUser;
  onCancel: () => void;
  autoFocusOnMount?: boolean;
}

const CommentInput: React.FC<CommentInputProps> = ({
  post,
  currentUser,
  onCancel,
  autoFocusOnMount = false,
}) => {
  const [commentBody, setCommentBody] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const commentMutation = useMutation({
    mutationFn: () =>
      authenticatedFetch(`posts`, {
        method: "POST",
        body: JSON.stringify({
          body: commentBody.trim(),
          parent_id: post.id,
        }),
      }),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["posts"] });
      await queryClient.cancelQueries({ queryKey: ["comments", post.id] });

      // Increase comment count optimistically
      queryClient.setQueryData<InfiniteData<any>>(["posts"], (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map((p: Post) =>
              p.id === post.id
                ? { ...p, comments_count: p.comments_count + 1 }
                : p
            ),
          })),
        };
      });

      // Add optimistic comment
      const tempId = Date.now();
      const optimisticComment = {
        id: tempId,
        user_id: currentUser.id,
        parent_id: post.id,
        body: commentBody.trim(),
        views: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        comments_count: 0,
        likes_count: 0,
        user: {
          id: currentUser.id,
          name: currentUser.name || "You",
        },
      };

      queryClient.setQueryData<any[]>(
        ["optimistic-comments", post.id],
        (old = []) => [...old, optimisticComment]
      );

      return { tempId };
    },

    onSuccess: () => {
      setCommentBody("");
      queryClient.invalidateQueries({ queryKey: ["comments", post.id] });
      queryClient.invalidateQueries({
        queryKey: ["optimistic-comments", post.id],
      });
    },

    onError: (_, __, context) => {
      // Rollback count
      queryClient.setQueryData<InfiniteData<any>>(["posts"], (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map((p: Post) =>
              p.id === post.id
                ? { ...p, comments_count: Math.max(0, p.comments_count - 1) }
                : p
            ),
          })),
        };
      });

      // Remove optimistic comment
      queryClient.setQueryData<any[]>(
        ["optimistic-comments", post.id],
        (old = []) => old.filter((c) => c.id !== (context as any)?.tempId)
      );
    },
  });

  useEffect(() => {
    if (autoFocusOnMount && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocusOnMount]);

  const handleSubmitComment = () => {
    if (!commentBody.trim()) return;
    commentMutation.mutate();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  return (
    <div className="mt-4 flex items-center gap-3">
      <div className="flex-1 relative">
        <input
          ref={inputRef}
          type="text"
          value={commentBody}
          onChange={(e) => setCommentBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a comment..."
          className="w-full px-4 py-2.5 pr-12 bg-gray-50 border border-gray-300 rounded-full text-sm focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-400 focus:bg-white transition-all duration-200"
          disabled={commentMutation.isPending}
        />
        <button
          onClick={handleSubmitComment}
          disabled={!commentBody.trim() || commentMutation.isPending}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          aria-label="Send comment"
        >
          <Send
            className={`w-5 h-5 transition-colors ${
              commentBody.trim() && !commentMutation.isPending
                ? "text-orange-600"
                : "text-gray-400"
            }`}
          />
        </button>
      </div>
      <button
        onClick={() => {
          onCancel();
          setCommentBody("");
        }}
        className="text-sm text-gray-500 hover:text-gray-700 font-medium px-2 py-1 transition"
        disabled={commentMutation.isPending}
      >
        Cancel
      </button>
    </div>
  );
};

export default CommentInput;
